// Frontend error monitoring — no third-party SDK. Uncaught errors and
// unhandled promise rejections are relayed to the analytics server's
// POST /client-errors endpoint, which emits them in Google Cloud Error
// Reporting's structured format. Incident detection then rides on the
// Error Reporting console + the alert policies in
// docs/runbooks/monitoring-and-alerts.md.
//
// Privacy: only the error message, stack, and page path are sent — no user
// identifiers, no cookies (credentials omitted), no form contents.

const IS_LOCAL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const CLOUD_URL = process.env.REACT_APP_API_URL || 'https://analytics-server-652526389082.europe-west2.run.app';
const SERVER_URL = IS_LOCAL ? 'http://localhost:8080' : CLOUD_URL;

const MAX_REPORTS_PER_SESSION = 20;
let reportsSent = 0;
const seen = new Set();

function send(payload) {
  if (reportsSent >= MAX_REPORTS_PER_SESSION) return;
  const key = `${payload.message}\n${(payload.stack || '').slice(0, 200)}`;
  if (seen.has(key)) return;
  seen.add(key);
  reportsSent += 1;

  try {
    // keepalive lets the request survive page unloads (sendBeacon is not
    // used: cross-origin beacons with JSON blobs are blocked by browsers).
    fetch(`${SERVER_URL}/client-errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {});
  } catch (_) {
    // Never let the error reporter itself throw.
  }
}

function buildPayload(message, stack) {
  return {
    message: String(message || 'Unknown client error').slice(0, 2000),
    stack: String(stack || '').slice(0, 8000),
    page: window.location.pathname,
    userAgent: navigator.userAgent,
    appVersion: process.env.REACT_APP_VERSION || '',
    ts: new Date().toISOString(),
  };
}

export function initErrorReporting() {
  window.addEventListener('error', (event) => {
    const err = event?.error;
    send(buildPayload(err?.message || event?.message, err?.stack));
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    send(buildPayload(
      reason?.message || (typeof reason === 'string' ? reason : 'Unhandled promise rejection'),
      reason?.stack
    ));
  });
}
